import React, { useState } from "react";
import axios from "axios";
import { useForm } from "@inertiajs/inertia-react";
import { SaveIcon } from "@heroicons/react/outline";
import { Container, PageTitle, TitleContainer } from "../../components/refactor/components";
import ASMSButton from "../../components/Button";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import deLocale from 'date-fns/locale/de';
import Spinner from "../../components/Spinner";

const Create = (props) => {
    const [open, toggleOpen] = useState(false);
    const [articles, setArticles] = useState(props.articles);
    const [articleErrors, setArticleErrors] = useState({});
    const filter = createFilterOptions();

    const { data, setData, post, processing, errors, transform } = useForm({
        market: "",
        article: "",
        amount: 1,
        storageLocation: "",
        storageType: "PALLET",
        createdAt: null
    });

    const getTimezoneOffset = value => value.getTimezoneOffset() * 60000;

    const localToUTC = dateTime => {
        return new Date(dateTime.getTime() - getTimezoneOffset(dateTime));
    };

    transform((data) => {
        return {
            ...data,
            article: Number.isInteger(data.article) ? data.article : data.article.id,
            createdAt: !!data.createdAt ? localToUTC(data.createdAt) : null,
        }
    });

    const createNewArticleForm = useForm({
        name: "",
        eanNumber: "",
        articleNumber: "",
        manufacturer: ""
    });

    const submit = () => {
        post("/admin/warehouse");
    };

    const createArticle = () => {
        axios.post("/admin/warehouse/article", createNewArticleForm.data)
            .then(async response => {
                setData("article", response.data.id);
                createNewArticleForm.reset();

                const articles = await axios.get("/admin/warehouse/article");
                setArticles(articles.data);

                toggleOpen(false);
            })
            .catch(error => {
                if (error.response) {
                    setArticleErrors(error.response.data);
                }
            })
    };

    const getHelperTextByProperty = (property) => {
        const errorObject = articleErrors.errors || errors;

        if (errorObject === undefined) {
            return null;
        }

        if (errorObject[property] !== undefined) {
            return errorObject[property];
        }

        return null;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
            <div className="space-y-3">
                <Container>
                    <PageTitle
                        title="Neuen Eintrag erstellen"
                        buttons={[
                            <ASMSButton
                                key="save"
                                IconComponent={processing ? Spinner : SaveIcon}
                                type="primary"
                                onClick={() => submit()}
                            >
                                Speichern
                            </ASMSButton>
                        ]}
                    />
                </Container>

                <TitleContainer title="Daten eingeben">
                    <div className="space-y-6">
                        <FormControl
                            variant="filled"
                            sx={{ width: 1 }}
                            error={!!getHelperTextByProperty("market")}
                        >
                            <InputLabel>Markt auswählen</InputLabel>
                            <Select
                                value={data.market}
                                onChange={e => setData("market", e.target.value)}
                                placeholder="Markt auswählen"
                                MenuProps={{ sx: { width: 1 } }}
                            >
                                {props.markets.map(market => (
                                    <MenuItem key={market.name + market.id} value={market.id}>{market.name}</MenuItem>
                                ))}
                            </Select>
                            {!!getHelperTextByProperty("market") && (
                                <FormHelperText>{getHelperTextByProperty("market")}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl
                            variant="filled"
                            sx={{ width: 1 }}
                            error={!!getHelperTextByProperty("article")}
                        >
                            <Autocomplete
                                renderInput={(params) => <TextField {...params}  variant="filled" label="Artikel suchen" />}
                                renderOption={(props, option) => <li {...props} key={option.id + option.name}>{option.name}</li>}
                                getOptionLabel={(option) => {
                                    if (typeof option === "string") return option;
                                    if (option.inputValue) return option.inputValue;
                                    return option.name;
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);

                                    if (params.inputValue !== '') {
                                        filtered.push({
                                            inputValue: params.inputValue,
                                            name: `"${params.inputValue}" neu anlegen`
                                        });
                                    }

                                    return filtered;
                                }}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === "string") {
                                        setTimeout(() => {
                                            toggleOpen(true);
                                            createNewArticleForm.setData("name", newValue);
                                        });
                                    } else if (newValue && newValue.inputValue) {
                                        toggleOpen(true);
                                        createNewArticleForm.setData("name", newValue.inputValue);
                                    } else {
                                        setData("article", newValue);
                                    }
                                }}
                                options={articles}
                                freeSolo
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                            />
                            {!!getHelperTextByProperty("article") ? (
                                <FormHelperText>{getHelperTextByProperty("article")}</FormHelperText>
                            ) : (
                                <FormHelperText>Suche nach einem Artikel oder gebe die Artikelbezeichnung ein, um einen Artikel neu anzulegen.</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl variant="filled" sx={{ width: 1 }}>
                            <TextField
                                variant="filled"
                                value={data.amount}
                                onChange={e => setData("amount", e.target.value)}
                                label="Menge"
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                error={!!getHelperTextByProperty("amount")}
                                helperText={getHelperTextByProperty("amount")}
                            />
                        </FormControl>

                        <FormControl
                            variant="filled"
                            sx={{ width: 1 }}
                            error={!!getHelperTextByProperty("storageLocation")}
                        >
                            <Autocomplete
                                onChange={(event, value) => {
                                    setData("storageLocation", value.id);
                                }}
                                renderInput={(params) => <TextField {...params} variant="filled" label="Lagerplatz suchen" />}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.name}>{option.name}</li>
                                )}
                                getOptionLabel={option => option.name}
                                options={props.storageLocations}
                            />
                            {!!getHelperTextByProperty("storageLocation") && (
                                <FormHelperText>{getHelperTextByProperty("storageLocation")}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl
                            variant="filled"
                            sx={{ width: 1 }}
                            error={!!getHelperTextByProperty("storageType")}
                        >
                            <FormLabel>Lagertyp</FormLabel>
                            <RadioGroup
                                row
                                value={data.storageType}
                                onChange={e => setData("storageType", e.target.value)}
                            >
                                <FormControlLabel value="PALLET" control={<Radio />} label="Palette" />
                                <FormControlLabel value="PARCEL" control={<Radio />} label="Paket" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl
                            variant="filled"
                            sx={{ width: 1 }}
                            error={!!getHelperTextByProperty("storageType")}
                        >
                            <MobileDatePicker
                                value={data.createdAt}
                                onChange={(newValue) => {
                                    setData("createdAt", newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        label="Einlagerungsdatum"
                                        helperText="Standardmäßig wird der heutige Tag als Einlagerungsdatum verwendet, alternativ kannst du ein anderes Datum auswählen."
                                    />
                                )}
                            />
                        </FormControl>
                    </div>
                </TitleContainer>

                <Dialog open={open} onClose={() => toggleOpen(false)}>
                    <DialogTitle>Neuen Artikel anlegen</DialogTitle>
                    <DialogContent className="space-y-3">
                        <TextField
                            error={!!getHelperTextByProperty("name")}
                            variant="filled"
                            value={createNewArticleForm.data.name}
                            onChange={e => createNewArticleForm.setData("name", e.target.value)}
                            label="Artikelbezeichnung"
                            fullWidth
                            helperText={getHelperTextByProperty("name")}
                        />
                        <TextField
                            error={!!getHelperTextByProperty("eanNumber")}
                            variant="filled"
                            value={createNewArticleForm.data.eanNumber}
                            onChange={e => createNewArticleForm.setData("eanNumber", e.target.value)}
                            label="EAN-Nummer"
                            fullWidth
                            helperText={getHelperTextByProperty("eanNumber")}
                        />
                        <TextField
                            error={!!getHelperTextByProperty("articleNumber")}
                            variant="filled"
                            value={createNewArticleForm.data.articleNumber}
                            onChange={e => createNewArticleForm.setData("articleNumber", e.target.value)}
                            label="Artikelnummer"
                            fullWidth
                            helperText={getHelperTextByProperty("articleNumber")}
                        />
                        <TextField
                            error={!!getHelperTextByProperty("manufacturer")}
                            variant="filled"
                            value={createNewArticleForm.data.manufacturer}
                            onChange={e => createNewArticleForm.setData("manufacturer", e.target.value)}
                            label="Hersteller"
                            fullWidth
                            helperText={getHelperTextByProperty("manufacturer")}
                        />

                        <div className="flex justify-end">
                            <Button onClick={() => createArticle()} variant="contained" disableElevation>Artikel anlegen</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </LocalizationProvider>
    );
};

export default Create;
